<template>
  <div class="ts-nav-pills">
    <!-- Sliding background indicator -->
    <div class="ts-nav-pills__slider" :style="sliderStyle" aria-hidden="true"></div>
    <!-- Tab buttons -->
    <button
      v-for="(item, index) in items"
      :key="index"
      type="button"
      class="ts-nav-pills__item"
      :class="{ 'ts-nav-pills__item--active': modelValue === index }"
      @click="$emit('update:modelValue', index)"
    >
      <i v-if="item.icon" class="material-icons ts-nav-pills__icon">{{ item.icon }}</i>
      <span v-if="!isMobile" :class="{ 'ms-1': item.icon }">{{ item.label }}</span>
    </button>
  </div>
</template>

<script>
export default {
  name: 'NavPills',
  props: {
    modelValue: {
      type: Number,
      default: 0,
    },
    // Each item: { label: String, icon?: String (material icon name) }
    items: {
      type: Array,
      required: true,
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      sliderLeft: 0,
      sliderWidth: 0,
      ready: false,
      isMobile: window.innerWidth <= 1024,
    };
  },
  computed: {
    sliderStyle() {
      if (!this.ready) return { opacity: 0 };
      return {
        opacity: 1,
        transform: `translateX(${this.sliderLeft}px)`,
        width: `${this.sliderWidth}px`,
      };
    },
  },
  watch: {
    modelValue() {
      this.$nextTick(() => this.syncSlider());
    },
    items() {
      this.$nextTick(() => this.syncSlider());
    },
  },
  mounted() {
    this.$nextTick(() => this.syncSlider());
    window.addEventListener('resize', this.handleResize);
    this._resizeObserver = new ResizeObserver(() => this.syncSlider());
    this._resizeObserver.observe(this.$el);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    if (this._resizeObserver) this._resizeObserver.disconnect();
  },
  methods: {
    handleResize() {
      this.isMobile = window.innerWidth <= 1024;
    },
    syncSlider() {
      const buttons = this.$el.querySelectorAll('.ts-nav-pills__item');
      const btn = buttons[this.modelValue];
      if (!btn) return;
      const cRect = this.$el.getBoundingClientRect();
      const bRect = btn.getBoundingClientRect();
      this.sliderLeft = bRect.left - cRect.left;
      this.sliderWidth = bRect.width;
      this.ready = true;
    },
  },
};
</script>

<style scoped>
.ts-nav-pills {
  position: relative;
  display: flex;
  align-items: stretch;
  padding: 4px;
  background: rgba(200, 200, 200, 0.2);
  border-radius: 0.5rem;
  overflow: hidden;
}

/* Sliding background pill */
.ts-nav-pills__slider {
  position: absolute;
  top: 4px;
  left: 0;
  height: calc(100% - 8px);
  border-radius: 0.375rem;
  background: #d9dfed;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
              width 0.3s cubic-bezier(0.4, 0, 0.2, 1),
              opacity 0.15s ease;
  pointer-events: none;
  z-index: 0;
}

/* Individual tab button */
.ts-nav-pills__item {
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 12px;
  border: none;
  background: transparent;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.5);
  white-space: nowrap;
  transition: color 0.25s ease;
  line-height: 1.5;
}

.ts-nav-pills__item--active {
  color: #344767;
}

.ts-nav-pills__icon {
  font-size: 1rem;
  vertical-align: middle;
}

/* Dark mode */
body.dark-version .ts-nav-pills {
  background: rgba(255, 255, 255, 0.08);
}

body.dark-version .ts-nav-pills__slider {
  background: linear-gradient(195deg, #FFCD3A, #E0B020);
}

body.dark-version .ts-nav-pills__item {
  color: rgba(255, 255, 255, 0.5);
}

body.dark-version .ts-nav-pills__item--active {
  color: #191919;
}
</style>
